import { useEffect, useState } from 'react'

/**
 * Animate a number from 0 up to `target` using requestAnimationFrame.
 *
 * Used by the Results screen's band-score reveal so the big "7.5" doesn't
 * snap in instantly while the ring around it is still mid-animation. With
 * count-up you get a visible "watch it climb" beat that matches the ring's
 * 1.2s fill — small thing, makes the score feel earned instead of issued.
 *
 * Easing is ease-out cubic so the number decelerates into its final value
 * (matches the ring's `cubic-bezier(0.2, 0.8, 0.2, 1)` timing), and the
 * effect re-runs whenever `target` changes (so re-rendering after a re-fetch
 * re-animates rather than silently swapping the number).
 *
 * @param target  The final value to animate to. `null` is treated as "not
 *                ready yet" — the hook returns 0 and does not start the
 *                animation until a real number arrives.
 * @param durationMs  Total animation length. Defaults to 1200ms to match
 *                    the BandRing's stroke-dashoffset transition.
 */
export function useCountUp(target: number | null, durationMs = 1200): number {
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (target === null) return

    const start = performance.now()
    let raf = 0

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs)
      const eased = 1 - (1 - progress) ** 3
      setValue(target * eased)
      if (progress < 1) {
        raf = requestAnimationFrame(tick)
      }
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, durationMs])

  return value
}
